import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Star, Quote, Award, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { SEOHead } from '@/components/seo/SEOHead';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { usePageDesign } from '@/hooks/usePageDesign';

const ReviewsPage: React.FC = () => {
  const { theme } = useTheme();
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('reviews');

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-reviews-page'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, address_city, address_state, google_rating, google_reviews_count')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['public-reviews'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('google_reviews')
        .select('*')
        .eq('status', 'approved')
        .gte('rating', 4)
        .order('is_featured', { ascending: false })
        .order('rating', { ascending: false })
        .order('reviewed_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';
  const location = [businessSettings?.address_city, businessSettings?.address_state].filter(Boolean).join(', ');
  const h1Title = `${companyName} Reviews${location ? ` | ${location}` : ''}`;
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '0';

  const renderStars = (rating: number, size = 20) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={
            i <= rating
              ? 'text-primary fill-primary'
              : 'text-muted-foreground/30'
          }
        />
      ))}
    </div>
  );

  return (
    <>
      <SEOHead
        title={`${companyName} Reviews - Customer Testimonials`}
        description={`Read ${reviews.length}+ verified customer reviews for ${companyName}${location ? ` in ${location}` : ''}. ${avgRating}-star average rating.`}
      />

      <ConfigurableHero
        heroStyle={heroStyle}
        title={h1Title}
        subtitle={`Read what our customers have to say about their experience with ${companyName}`}
        showBreadcrumbs={showBreadcrumbs}
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Reviews' }]}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
        showRating
        rating={Number(avgRating)}
        reviewCount={reviews.length}
      />

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap items-center justify-center gap-8 mb-16 p-8 surface rounded-2xl"
          >
            <div className="text-center">
              <p className="text-5xl font-bold text-primary">{avgRating}</p>
              <div className="flex justify-center mt-2">{renderStars(Math.round(Number(avgRating)))}</div>
              <p className="text-sm text-muted-foreground mt-1">Average Rating</p>
            </div>
            <div className="w-px h-16 bg-border hidden sm:block" />
            <div className="text-center">
              <p className="text-5xl font-bold text-foreground">{reviews.length}</p>
              <p className="text-sm text-muted-foreground mt-1">Total Reviews</p>
            </div>
            <div className="w-px h-16 bg-border hidden sm:block" />
            <div className="text-center">
              <p className="text-5xl font-bold text-foreground">
                {reviews.filter(r => r.rating === 5).length}
              </p>
              <p className="text-sm text-muted-foreground mt-1">5-Star Reviews</p>
            </div>
          </motion.div>

          {/* Reviews Grid */}
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No reviews yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3) }}
                  className="group relative surface p-6 space-y-4 overflow-hidden"
                >
                  {review.is_featured && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-primary/10 text-primary border-primary/20 text-xs gap-1">
                        <Award className="h-3 w-3" /> Featured
                      </Badge>
                    </div>
                  )}

                  <Quote
                    size={40}
                    className="absolute -top-1 -left-1 opacity-5 text-primary"
                  />

                  <div className="relative z-10">
                    {renderStars(review.rating)}

                    <p className="text-foreground text-sm leading-relaxed mt-4 italic">
                      "{review.review_text || 'Great experience!'}"
                    </p>

                    {review.review_reply && (
                      <div className="mt-3 pl-4 border-l-2 border-primary/30">
                        <p className="text-xs text-muted-foreground font-medium mb-1">Response from {companyName}:</p>
                        <p className="text-xs text-muted-foreground italic">{review.review_reply}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-4 mt-4 border-t border-border/30">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-sm text-primary">
                        {review.reviewer_name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-foreground">{review.reviewer_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {review.reviewer_location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {review.reviewer_location}
                            </span>
                          )}
                          {review.reviewed_at && (
                            <span>{new Date(review.reviewed_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize shrink-0">
                        {review.source || 'Google'}
                      </Badge>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ReviewsPage;
